/**
 * Sticky add to cart (bottom bar)
 *
 * The bar never adds to cart by itself: it selects the options and clicks the main "Add to cart" button of the
 * product form, so the theme keeps handling variants, quantity, cart drawer and errors exactly as before.
 */
(function () {
  'use strict';

  var bar = document.querySelector('[data-sticky-atc]');
  var section = document.querySelector('[data-section-type="product"]');

  if (!bar || !section) {
    return;
  }

  var mainButton = section.querySelector('.ProductForm__AddToCart');
  var jsonElement = bar.querySelector('[data-sticky-atc-json]');

  if (!mainButton || !jsonElement) {
    return;
  }

  var data = JSON.parse(jsonElement.textContent);
  var select = bar.querySelector('[data-sticky-atc-select]');
  var button = bar.querySelector('[data-sticky-atc-button]');
  var priceElement = bar.querySelector('[data-sticky-atc-price]');
  var compareElement = bar.querySelector('[data-sticky-atc-compare]');
  var imageElement = bar.querySelector('[data-sticky-atc-image]');
  var loadingTimeout = null;

  /**
   * ---------------------------------------------------------------------------------------------------
   * HELPERS
   * ---------------------------------------------------------------------------------------------------
   */

  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    format = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision === undefined ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';

      return dollars + centsPart;
    }

    var value = '';

    switch (format.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholderRegex, value);
  }

  function getVariantById(id) {
    id = parseInt(id, 10);

    for (var i = 0; i < data.variants.length; i++) {
      if (data.variants[i].id === id) {
        return data.variants[i];
      }
    }

    return null;
  }

  function sizedImageUrl(src, width) {
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function updateBodyOffset() {
    document.documentElement.style.setProperty('--sticky-atc-height', bar.offsetHeight + 'px');
  }

  /**
   * ---------------------------------------------------------------------------------------------------
   * SHOW / HIDE THE BAR
   * Visible whenever the main "Add to cart" button is out of the viewport (above or below)
   * ---------------------------------------------------------------------------------------------------
   */

  function setVisible(visible) {
    bar.classList.toggle('is-visible', visible);
    bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    document.body.classList.toggle('has-sticky-atc', visible);

    if (visible) {
      updateBodyOffset();
    }
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      var entry = entries[0];
      setVisible(!entry.isIntersecting);
    }).observe(mainButton);
  } else {
    var onScroll = function () {
      var rect = mainButton.getBoundingClientRect();
      setVisible(rect.bottom < 0 || rect.top > window.innerHeight);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  window.addEventListener('resize', updateBodyOffset);

  /**
   * ---------------------------------------------------------------------------------------------------
   * KEEP THE BAR IN SYNC WITH THE MAIN FORM
   * ---------------------------------------------------------------------------------------------------
   */

  function renderVariant(variant) {
    if (select && variant) {
      select.value = variant.id;
    }

    if (!variant) {
      button.setAttribute('disabled', 'disabled');
      button.textContent = data.unavailableLabel;
      return;
    }

    priceElement.textContent = formatMoney(variant.price);

    if (variant.compare_at_price > variant.price) {
      priceElement.classList.add('Price--highlight');
      compareElement.textContent = formatMoney(variant.compare_at_price);
      compareElement.style.display = '';
    } else {
      priceElement.classList.remove('Price--highlight');
      compareElement.style.display = 'none';
    }

    if (imageElement && variant.featured_image) {
      imageElement.src = sizedImageUrl(variant.featured_image.src, 120);
      imageElement.alt = variant.featured_image.alt || '';
    }

    if (variant.available) {
      button.removeAttribute('disabled');
      button.textContent = data.addToCartLabel;
    } else {
      button.setAttribute('disabled', 'disabled');
      button.textContent = data.soldOutLabel;
    }
  }

  section.addEventListener('variant:changed', function (event) {
    renderVariant(event.detail.variant);
  });

  /**
   * ---------------------------------------------------------------------------------------------------
   * VARIANT CHANGE FROM THE BAR -> MAIN FORM
   * We click the matching swatch / popover value so the theme runs its own variant logic
   * ---------------------------------------------------------------------------------------------------
   */

  function selectOptionInMainForm(position, value) {
    var radios = section.querySelectorAll('.ProductForm__Option input[type="radio"][data-option-position="' + position + '"]');

    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value === value) {
        if (!radios[i].checked) {
          radios[i].click();
        }

        return true;
      }
    }

    var popoverValues = section.querySelectorAll('[data-action="select-value"][data-option-position="' + position + '"]');

    for (var j = 0; j < popoverValues.length; j++) {
      if (popoverValues[j].getAttribute('data-value') === value) {
        if (!popoverValues[j].classList.contains('is-selected')) {
          popoverValues[j].click();
        }

        return true;
      }
    }

    return false;
  }

  if (select) {
    select.addEventListener('change', function () {
      var variant = getVariantById(select.value);

      if (!variant) {
        return;
      }

      var synced = true;

      for (var position = 1; position <= 3; position++) {
        var value = variant['option' + position];

        if (value !== null && value !== undefined) {
          synced = selectOptionInMainForm(position, value) && synced;
        }
      }

      // Fallback (eg. color carousel selector): set the variant id directly on the form
      if (!synced) {
        var idInput = section.querySelector('form[action*="/cart/add"] [name="id"]');

        if (idInput) {
          idInput.value = variant.id;
        }

        renderVariant(variant);
      }
    });
  }

  /**
   * ---------------------------------------------------------------------------------------------------
   * ADD TO CART
   * ---------------------------------------------------------------------------------------------------
   */

  function stopLoading() {
    clearTimeout(loadingTimeout);
    button.classList.remove('is-loading');
  }

  button.addEventListener('click', function () {
    if (button.hasAttribute('disabled') || mainButton.hasAttribute('disabled')) {
      return;
    }

    button.classList.add('is-loading');
    loadingTimeout = setTimeout(stopLoading, 4000);
    mainButton.click();
  });

  document.addEventListener('product:added', stopLoading);
  document.addEventListener('theme:loading:end', stopLoading);
})();
